/*
* Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
*/
import React, {memo, useCallback} from 'react';
import {createStyles, withStyles} from '@material-ui/core/styles';
import MaterialTab from '@material-ui/core/Tab';
import CloseIcon from '@material-ui/icons/Close';
import classnames from 'classnames';
import {GenericObject} from '@ion/core/src/types/entities';
import useExtendStyles from '../../hooks/useExtendStyles';
import {dlsAlt, dlsColor, dlsTypography} from '../../theme/theme';
import {DLSTheme} from '../../types/types';
import Tooltip from '../tooltip/Tooltip';

// defining props definition for Tab component...
export interface TabProps {
  tab: {tabHeader: string, path?: string, tag?: string},
  onClose?: Function,
  onClick?: Function,
  customTabs?: boolean,
  customStyles?: boolean,
  isCondensedVariant?: boolean,
  overrides?: (theme: DLSTheme) => GenericObject,
  minWidth?: number | string,
  maxWidth?: number | string,
  index?: number,
  showToolTip?: boolean,
  showIcon?: boolean
};

// defining the style definition for Tab component...
const useStyles = () => {
  const {
    fontFamily,
    fontWeight,
    fontSize,
    lineHeight,
    verticalAlign,
    opacity,
    letterSpacing
  } = dlsTypography('tabs');
  return {
    root : {
      flexGrow : 1,
      color    : dlsColor('black'),
      minWidth : '200px',
      fontFamily,
      fontWeight,
      fontSize,
      lineHeight,
      verticalAlign,
      opacity,
      letterSpacing
    },
    backgroundStyle : {
      backgroundColor : dlsColor('grey6')
    },
    altBackgroundStyle : {
      maxWidth        : '50%',
      backgroundColor : dlsColor('white')
    },
    endAdornment : {
      position : 'absolute',
      right    : 0
    },
    tag : {
      backgroundColor : dlsColor('caBlue'),
      borderRadius    : '20px',
      ...dlsAlt('components', 'ionpreviewText', 'new'),
      display         : 'flex',
      padding         : '3px'
    }
  };
};

let customStyle = false;

const TabStyle = withStyles(() =>
  createStyles({
    root : {
      '&$selected' : {
        color : dlsColor('black')
      }
    },
    selected : {},
    wrapper  : customStyle ? {
      textOverflow : 'ellipsis',
      whiteSpace   : 'nowrap',
      overflow     : 'hidden',
      display      : 'block',
      padding      : '0 12px'
    } : {}
  })
)(MaterialTab);

const ReportTooltip = function(props) {
  return <Tooltip arrow {...props} />;
};

/**
 * Defines the tab component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *  import {Tab} from '@ion/dls/src/components/tabs';
 *
 *  <Tab tab={tab} onClose={onClose} index={index} />
 *
 * ```
 */
export const Tab = ({
  tab,
  onClose,
  onClick,
  customTabs,
  customStyles,
  isCondensedVariant,
  overrides,
  minWidth,
  maxWidth,
  index = 0,
  showToolTip = true,
  showIcon = false
}: TabProps) => {
  customStyle        = customTabs;
  const tabViewStyles = useExtendStyles(useStyles, overrides);
  const classes       = tabViewStyles();

  const renderTag = useCallback((tag: string) => (
    <sup className={classes.tag}>{tag}</sup>
  ), [classes]);

  const onIconClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose?.(e, index);
  }, [index, onClose]);

  const onChangeIndex = useCallback((e) => {
    onClick(e, index);
  }, [index, onClick]);

  return (
    <TabStyle className={
      classnames(
        classes.root,
        classes.backgroundStyle,
        isCondensedVariant && classes.altBackgroundStyle
      )}
    key={`${index}-${tab.tabHeader}`}
    onClick={onChangeIndex}
    style={{
      paddingLeft : customStyles && index === 0 ? 0 : '12px',
      minWidth,
      maxWidth
    }}
    label={showToolTip ? (
      <ReportTooltip title={(
        <div style={{textTransform: 'uppercase', fontSize: 10}}>
          {tab.tabHeader}
        </div>
      )}>
        <span>{tab.tabHeader}</span>
      </ReportTooltip>
    ) : (
      <>{tab.tabHeader}{tab.tag?.split('').map(() => <>&nbsp;</>)}</>
    )}
    icon={(showIcon || tab.tag) && (
      <div className={classes.endAdornment}
        onClick={onIconClick}>
        {tab.tag ? renderTag(tab.tag) : <CloseIcon />}
      </div>
    )} />
  );
};

export default memo(Tab);
